import React, { useRef, useState, useCallback, useEffect } from 'react';
import { TextField, type TextFieldProps, Popper, Paper, Typography, Box } from '@mui/material';
// @ts-ignore — no type declarations for this package
import avro from 'nodejs-avro-phonetic';
import { useFormNavigation, NavFieldMenu, type NavFieldMenuProps } from './FormNavigation';

/**
 * A MUI TextField that converts Avro phonetic input to Bangla on Space/Enter,
 * and participates in FormNavigation keyboard navigation.
 *
 * How it works:
 *  - Space/Enter commits the last word via avro.parse() (same as BanglaPhoneticInput).
 *  - Enter additionally moves focus to the next nav field after conversion.
 *  - ArrowDown / ArrowUp navigate to next / prev nav field.
 *  - Shows an enhanced real-time Bangla preview just below the cursor.
 */

type Props = Omit<TextFieldProps, 'onChange' | 'value'> & NavFieldMenuProps & {
  value: string;
  onChange: (value: string) => void;
};

export default function NavBanglaPhoneticInput({
  value,
  onChange,
  inputProps,
  onKeyDown: externalOnKeyDown,
  onFocus: externalOnFocus,
  onBlur: externalOnBlur,
  onHistoryClick,
  historyDisabled,
  onEditTagListClick,
  tagListTitle,
  onResetClick,
  resetDisabled,
  hideMenu,
  InputProps,
  ...rest
}: Props) {
  const [display, setDisplay] = useState(value);
  const [preview, setPreview] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [caretX, setCaretX] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const mirrorRef = useRef<HTMLSpanElement>(null);
  const { navigate } = useFormNavigation('text');

  // Keep display in sync when the parent resets/clears the value.
  const prevValueRef = useRef(value);
  if (value !== prevValueRef.current) {
    prevValueRef.current = value;
    setDisplay(value);
  }

  const updatePreviewAndCaret = useCallback((textOverride?: string, cursorOverride?: number) => {
    const input = inputRef.current;
    const mirror = mirrorRef.current;
    if (!input || !isFocused) {
      setPreview('');
      return;
    }

    const text = textOverride !== undefined ? textOverride : display;
    const cursor = cursorOverride !== undefined ? cursorOverride : (input.selectionStart ?? text.length);
    const before = text.substring(0, cursor);
    const wordMatch = before.match(/(\S+)$/);

    // Calculate caret position for Popper offset
    if (mirror) {
      const style = window.getComputedStyle(input);
      mirror.style.font = style.font;
      mirror.style.fontFamily = style.fontFamily;
      mirror.style.fontSize = style.fontSize;
      mirror.style.fontWeight = style.fontWeight;
      mirror.style.letterSpacing = style.letterSpacing;
      mirror.style.textTransform = style.textTransform;
      mirror.textContent = before.replace(/ /g, '\u00a0');
      
      const paddingLeft = parseFloat(style.paddingLeft);
      setCaretX(mirror.offsetWidth + paddingLeft);
    }

    if (wordMatch) {
      const word = wordMatch[1];
      const parsed = avro.parse(word);
      if (parsed !== word) {
        setPreview(parsed);
      } else {
        setPreview('');
      }
    } else {
      setPreview('');
    }
  }, [display, isFocused]);

  useEffect(() => {
    updatePreviewAndCaret();
  }, [updatePreviewAndCaret]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const isSpace = e.key === ' ';
      const isEnter = e.key === 'Enter';

      if (isSpace || isEnter) {
        const input = inputRef.current;
        if (!input) return;

        const cursor = input.selectionStart ?? display.length;
        const before = display.substring(0, cursor);
        const after = display.substring(cursor);

        const wordMatch = before.match(/(\S+)$/);

        if (wordMatch) {
          const word = wordMatch[1];
          const wordStart = cursor - word.length;
          const converted: string = avro.parse(word);
          const separator = isEnter ? '' : ' ';
          const newDisplay =
            display.substring(0, wordStart) + converted + separator + after;

          e.preventDefault();
          setDisplay(newDisplay);
          prevValueRef.current = newDisplay;
          onChange(newDisplay);

          const newCursor = wordStart + converted.length + separator.length;
          requestAnimationFrame(() => {
            input.selectionStart = input.selectionEnd = newCursor;
            updatePreviewAndCaret(newDisplay, newCursor);
          });
        } else {
          if (isSpace || isEnter) {
             const input = inputRef.current;
             if (input) {
               const cursor = input.selectionStart ?? display.length;
               const before = display.substring(0, cursor);
               const after = display.substring(cursor);
               const separator = isSpace ? ' ' : '';
               const newDisplay = before + separator + after;

               e.preventDefault();
               setDisplay(newDisplay);
               prevValueRef.current = newDisplay;
               onChange(newDisplay);

               const newCursor = cursor + separator.length;
               requestAnimationFrame(() => {
                 input.selectionStart = input.selectionEnd = newCursor;
                 updatePreviewAndCaret(newDisplay, newCursor);
               });
             }
          }
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (inputRef.current) navigate('next', inputRef.current);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (inputRef.current) navigate('prev', inputRef.current);
      }

      externalOnKeyDown?.(e);
    },
    [display, onChange, navigate, externalOnKeyDown, updatePreviewAndCaret],
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && inputRef.current) {
        navigate('next', inputRef.current);
      }
    },
    [navigate],
  );

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Hidden span for caret tracking */}
      <span
        ref={mirrorRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'pre',
          pointerEvents: 'none',
        }}
      />
      
      <TextField
        {...rest}
        inputRef={inputRef}
        value={display}
        onChange={(e) => setDisplay(e.target.value)}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={(e) => {
          setIsFocused(true);
          externalOnFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          // Convert any remaining phonetic text on blur
          const converted = avro.parse(display);
          if (converted !== display) {
            setDisplay(converted);
            prevValueRef.current = converted;
            onChange(converted);
          }
          externalOnBlur?.(e);
        }}
        InputProps={{
          ...(InputProps as object),
          startAdornment: (
            <>
              <NavFieldMenu
                onHistoryClick={onHistoryClick}
                historyDisabled={historyDisabled}
                onEditTagListClick={onEditTagListClick}
                tagListTitle={tagListTitle}
                onResetClick={onResetClick}
                resetDisabled={resetDisabled}
                hideMenu={hideMenu}
              />
              {(InputProps as any)?.startAdornment}
            </>
          ),
        }}
        inputProps={{
          ...inputProps,
          'data-nav-field': 'text',
          lang: 'bn',
        }}
      />

      <Popper
        open={isFocused && !!preview}
        anchorEl={inputRef.current}
        placement="bottom-start"
        modifiers={[
          {
            name: 'offset',
            options: {
              // Shift the popper based on caret position
              // We use caretX as the horizontal offset
              offset: [caretX - 10, 8],
            },
          },
        ]}
        sx={{ zIndex: 1500, pointerEvents: 'none' }}
      >
        <Paper 
          elevation={4} 
          sx={{ 
            p: '2px 10px', 
            backgroundColor: '#e3f2fd', 
            color: '#000',
            borderRadius: '4px',
            boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
            border: '1px solid #bbdefb',
          }}
        >
          <Typography sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.4 }}>
            {preview}
          </Typography>
        </Paper>
      </Popper>
    </Box>
  );
}
